"use client";

import { cn } from "@/lib/utils";

export type GlassVariant = "emerald" | "cyan" | "rose" | "amber";

interface LiquidGlassCardProps extends React.ComponentProps<"div"> {
  /** Color variant for the glow and accents */
  variant?: GlassVariant;
  /** Show pulsing glow effect */
  pulse?: boolean;
  /** Show ring highlight (for success/active states) */
  ring?: boolean;
  /** Intensity of the glow effect */
  glowIntensity?: "subtle" | "normal" | "intense";
}

const glowColors: Record<GlassVariant, string> = {
  emerald: "from-primary/20 via-success/10 to-info/20",
  cyan: "from-info/20 via-info/10 to-info/20",
  rose: "from-destructive/20 via-viz-accent/10 to-destructive/20",
  amber: "from-warning/20 via-warning/10 to-warning/20",
};

const ringColors: Record<GlassVariant, string> = {
  emerald: "ring-primary/50",
  cyan: "ring-info/50",
  rose: "ring-destructive/50",
  amber: "ring-warning/50",
};

const glowIntensities = {
  subtle: "opacity-20",
  normal: "opacity-30",
  intense: "opacity-40",
};

/**
 * LiquidGlassCard - A reusable refractive glass card with ambient glow effects
 *
 * Features:
 * - Shared elevated liquid-glass material
 * - SVG refraction with a tokenized CSS fallback
 * - Gradient glow halo
 * - Multiple color variants
 * - Optional pulsing animation
 */
export function LiquidGlassCard({
  children,
  className,
  variant = "emerald",
  pulse = false,
  ring = false,
  glowIntensity = "normal",
  ...props
}: LiquidGlassCardProps) {
  return (
    <div
      data-slot="liquid-glass-card"
      className={cn("relative", className)}
      {...props}
    >
      {/* Outer glow layer */}
      <div
        className={cn(
          "absolute -inset-px rounded-2xl blur-2xl",
          "bg-gradient-to-br",
          glowColors[variant],
          glowIntensities[glowIntensity],
          pulse && "animate-pulse",
        )}
      />

      {/* Glass container */}
      <div
        data-liquid-glass="elevated"
        className={cn(
          "relative overflow-hidden rounded-2xl",
          "border",
          ring && `ring-2 ${ringColors[variant]}`,
        )}
      >
        <div className="relative">{children}</div>
      </div>
    </div>
  );
}

LiquidGlassCard.displayName = "LiquidGlassCard";
